import { typographyVariant } from '@heidi/design-system/typography';
import type { TranslationKey } from '@heidi/runtime/i18n';
import { useTranslations } from '@heidi/runtime/i18n';
import { cn } from '@heidi/utils';
import { Pen } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

export interface InlineEditableNameProps {
  value: string;
  placeholder?: string;
  onChange: (name: string) => void;
  className?: string;
}

/**
 * Inline-editable collection name displayed in the wizard header.
 *
 * Always renders a single `<input>` styled as transparent h3 text — no
 * DOM swap between display and edit states, so there is zero layout shift
 * when the user clicks to edit.
 *
 * - Click / focus: caret appears, text is selected
 * - Blur or Enter: confirms the edit
 * - Escape: cancels and reverts to previous value
 */
export const InlineEditableName = forwardRef<HTMLInputElement, InlineEditableNameProps>(
  ({ value, placeholder, onChange, className }, ref) => {
    const { t } = useTranslations();
    const effectivePlaceholder =
      placeholder ?? t('library.wizard.defaultCollectionName' as TranslationKey);
    const [editValue, setEditValue] = useState(value);
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);
    const valueOnFocusRef = useRef(value);
    const skipBlurRef = useRef(false);

    // Sync when external value changes while not focused.
    // Skip if we have a pending optimistic value to avoid flashing the old title.
    const pendingValueRef = useRef<string | null>(null);

    useEffect(() => {
      if (!isFocused) {
        if (pendingValueRef.current !== null) {
          // Clear pending once the prop catches up
          if (value === pendingValueRef.current) {
            pendingValueRef.current = null;
          }
          // Don't overwrite with stale prop while waiting
          return;
        }
        setEditValue(value);
      }
    }, [value, isFocused]);

    const handleFocus = useCallback(() => {
      valueOnFocusRef.current = value;
      setEditValue(value);
      setIsFocused(true);
      // Select all text so the user can start typing to replace
      requestAnimationFrame(() => {
        inputRef.current?.select();
      });
    }, [value]);

    const confirmEdit = useCallback(() => {
      const trimmed = editValue.trim();
      if (trimmed && trimmed !== valueOnFocusRef.current) {
        pendingValueRef.current = trimmed;
        onChange(trimmed);
      } else {
        // Revert to last confirmed value
        setEditValue(valueOnFocusRef.current);
      }
      setIsFocused(false);
    }, [editValue, onChange]);

    const handleBlur = useCallback(() => {
      if (skipBlurRef.current) {
        skipBlurRef.current = false;
        setIsFocused(false);
        return;
      }
      confirmEdit();
    }, [confirmEdit]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          skipBlurRef.current = true;
          confirmEdit();
          inputRef.current?.blur();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          skipBlurRef.current = true;
          setEditValue(valueOnFocusRef.current);
          setIsFocused(false);
          inputRef.current?.blur();
        }
      },
      [confirmEdit]
    );

    const sharedStyles = cn(
      typographyVariant({ variant: 'h3' }),
      'min-w-0 border-0 bg-transparent text-left font-exposure outline-none',
      className
    );

    return (
      <div className="group inline-flex max-w-full items-center gap-2">
        <div className="inline-grid min-w-[3ch] max-w-full">
          <span
            className={cn(
              sharedStyles,
              'invisible col-start-1 row-start-1 whitespace-pre'
            )}
            aria-hidden
          >
            {editValue || effectivePlaceholder}
          </span>
          <input
            ref={inputRef}
            type="text"
            size={1}
            value={editValue}
            placeholder={effectivePlaceholder}
            onChange={(e) => setEditValue(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={cn(
              sharedStyles,
              'col-start-1 row-start-1 cursor-text truncate transition-colors duration-150',
              'placeholder:text-text-tertiary',
              editValue ? 'text-text-primary' : 'text-text-tertiary'
            )}
            title={t('library.wizard.editNameTooltip' as TranslationKey)}
          />
        </div>
        <Pen
          size={14}
          aria-hidden
          data-testid="inline-editable-name-edit-icon"
          className={cn(
            'shrink-0 text-text-tertiary transition-opacity duration-200',
            isFocused ? 'opacity-0' : 'opacity-60 group-hover:opacity-100'
          )}
        />
      </div>
    );
  }
);

InlineEditableName.displayName = 'InlineEditableName';
